import { bind, wire } from 'hyperhtml/esm';
import { KatalComponent } from '../../shared/base';
import getString from './get-string';

/**
 * @component {kat-radiobutton-group} KatalRadioButtonGroup A form control that allows a user to select one of several options.
 * @guideline Do All input fields should have a label.
 * @event change This event is fired whenever the selected radio button changes.
 * @event focus This event is fired whenever a radio button in the group is focused and the last focused element was outside the group.
 * @event blur This event is fired whenever focus leaves any of the radio buttons in the group to an element outside of the group.
 * @status Production
 * @theme flo
 * @example Basic {"name": "group1", "value": "one", "label":"Radio Button Group", "options": [{"value": "one", "label": "First option"}, {"value": "two", "label": "Second option"},{"value": "three", "label": "Third option"}]}
 * @example Disabled {"name": "group1", "disabled": true, "value": "one", "label":"Radio Button Group", "options": [{"value": "one", "label": "First option"}, {"value": "two", "label": "Second option"}, {"value": "three", "label": "Third option"}]}
 * @example Complex {"name": "group1", "value": "one", "label":"Radio Button Group", "constraint-label": "Longer description of the group.", "error-label": "Please select a option", "info-tooltip": "Additional information to display in a tooltip", "options": [{"value": "one", "label": "First option", "constraintLabel": "secondary label on first option"}, {"value": "two", "label": "Second option (disabled)", "disabled": true}, {"value": "three", "label": "Third option"}]}
 */
class KatalRadioButtonGroup extends KatalComponent {
  static get observedAttributes() {
    return [
      'name',
      'value',
      'options',
      'label',
      'constraint-label',
      'error-label',
      'disabled',
      'info-tooltip',
    ];
  }

  constructor() {
    super(KatalRadioButtonGroup.observedAttributes);

    this._options = [];
    this._lastFocusedRadio = null;
    this._lastBlurredRadio = null;

    this._handleRadioChangeClosure = this.handleRadioChange.bind(this);
    this._handleRadioFocusClosure = this.handleRadioFocus.bind(this);
    this._handleRadioBlurClosure = this.handleRadioBlur.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @required
   * @classprop {string} name The name used to group radio buttons.
   */
  get name() {
    return this.getAttribute('name');
  }
  set name(value) {
    this.removeOrSetAttribute('name', value);
  }

  /**
   * @classprop {string} value If set then the child button with the same value will be selected.
   */
  get value() {
    return this.getAttributeOrDefault('value');
  }
  set value(value) {
    this.removeOrSetAttribute('value', value);
  }

  /**
   * @classprop {json} options An array of options used to populate the group with radio buttons. Each item in the array is a object and the properties are assigned to the radio buttons as properties. e.g. [{"label":"First","value":"1", "constraintLabel": "secondary label"}, {"label":"Second","value":"2"}]
   * @jsonExample - {"options": "[{"label":"First","value":"1"}, {"label":"Second","value":"2"}]"}
   * @required
   */
  get options() {
    return this._options;
  }
  set options(value) {
    this._options = this.handleArrayAttribute('options', value);
    this.render();
  }

  /**
   * @classprop {string} label The title of the whole group.
   */
  get label() {
    return this.getAttributeOrDefault('label', '');
  }
  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @classprop {string} constraint-label A message explaining the purpose of the group.
   */
  get constraintLabel() {
    return this.getAttributeOrDefault('constraint-label', '');
  }
  set constraintLabel(value) {
    this.removeOrSetAttribute('constraint-label', value);
  }

  /**
   * @classprop {string} error-label The error message to display for the group.
   */
  get errorLabel() {
    return this.getAttributeOrDefault('error-label', '');
  }
  set errorLabel(value) {
    this.removeOrSetAttribute('error-label', value);
  }

  /**
   * @classprop {boolean} disabled If set will disable all radio buttons in the group.
   */
  get disabled() {
    return this.getBooleanAttribute('disabled');
  }
  set disabled(value) {
    this.removeOrSetAttribute('disabled', value);
  }

  /**
   * @classprop {string} info-tooltip A message used to provide additional information. This will be placed inside a tooltip on an icon next to the label.
   */
  get infoTooltip() {
    return this.getAttributeOrDefault('info-tooltip', '');
  }
  set infoTooltip(value) {
    this.removeOrSetAttribute('info-tooltip', value);
  }

  // Note: For the spacing attribute we just need the JSON metadata not the actual getter and setter.
  /**
   * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
   * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
   * more details.
   */
  get katal12SpacingFixes() {
    return this.getBooleanAttribute('katal-1-2-spacing-fixes');
  }
  set katal12SpacingFixes(value) {
    return this.setBooleanAttribute('katal-1-2-spacing-fixes', value);
  }

  handleRadioChange(evt) {
    const radio = evt.target;
    this.value = radio.value;

    this.dispatchCustomEvent('change', { value: this.value });
  }

  handleRadioFocus(evt) {
    evt.stopPropagation();
    this._lastFocusedRadio = evt.target;

    if (this._lastBlurredRadio === null) {
      this.dispatchCustomEvent('focus');
    }
  }

  handleRadioBlur(evt) {
    evt.stopPropagation();
    this._lastBlurredRadio = evt.target;
    this._lastFocusedRadio = null;

    // The focus handler above should be executed before this setTimeout callback
    setTimeout(() => {
      if (this._lastFocusedRadio === null) {
        this.dispatchCustomEvent('blur');
      }
      this._lastBlurredRadio = null;
    }, 0);
  }

  /**
   * Programmatically focus the group. Focuses the first radio button.
   * @katalmethod
   */
  focus() {
    const first = this.querySelector('kat-radiobutton');
    if (first) {
      first.focus();
    }
  }

  /**
   * Programmatically blur any focused radio buttons.
   * @katalmethod
   */
  blur() {
    const buttons = this.querySelectorAll('kat-radiobutton');
    for (let i = 0; i < buttons.length; i++) {
      buttons[i].blur();
    }
  }

  render() {
    const radioButtons = this._options.map(option => {
      const radio = wire(option)`<kat-radiobutton
                name=${this.name}
                checked=${option.value === this.value}
                disabled=${this.disabled}
                onchange=${this._handleRadioChangeClosure}
                onfocus=${this._handleRadioFocusClosure}
                onblur=${this._handleRadioBlurClosure}
            />`;
      Object.keys(option).forEach(attr => {
        if (attr === 'name') {
          return;
        }

        if (attr === 'disabled') {
          radio.disabled = this.disabled || option.disabled;
        } else {
          radio[attr] = option[attr];
        }
      });
      return radio;
    });

    const ariaTrigger = getString(
      'katal-radiobutton-group-aria-tooltip-trigger'
    );
    const tooltip =
      this.infoTooltip === ''
        ? null
        : wire()`<kat-tooltip
                trigger-icon="help_outline"
                size="small"
                position="top"
                label=${this.infoTooltip}
                kat-aria-trigger=${ariaTrigger}
            />`;
    const error =
      this.errorLabel === ''
        ? null
        : wire()`<kat-statusindicator label=${this.errorLabel} variant="error" />`;

    const constraintLabel =
      this.constraintLabel === ''
        ? null
        : wire()`<div class="kat-radiobutton-group-constraint-label">${this.constraintLabel}</div>`;

    return bind(this)`
            <div class="kat-radiobutton-group-label">
                <span>${this.label}</span>
                ${tooltip}
            </div>
            ${constraintLabel}
            ${error}
            <div role="radiogroup" aria-disabled=${this.disabled}>
                ${radioButtons}
            </div>
        `;
  }

  // Respond to attribute changes.
  attributeChangedCallback(attr, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (attr === 'options') {
        this.options = newValue;
      } else {
        this.render();
      }
    }
  }
}

KatalComponent.register('kat-radiobutton-group', KatalRadioButtonGroup);
